import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HeaderDto } from '@shared/dto/auth/Header.dto';
import { BehaviorSubject, filter } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AuthenticationDto } from '../../.api-client/models/authentication-dto';
import { AuthenticationApiService } from '../../.api-client/services/authentication-api.service';
import { SKIP_HEADER_INTERCEPTOR } from '@delab/ngx-ixconnect-components';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly _headerSource = new BehaviorSubject<HeaderDto[] | undefined>(undefined);
    public readonly headerObs = this._headerSource.asObservable();

    private readonly _authSource = new BehaviorSubject<AuthenticationDto | undefined>(undefined);
    public readonly authObs = this._authSource.asObservable();

    private readonly _adminSource = new BehaviorSubject<boolean>(false);
    public readonly adminObs = this._adminSource.asObservable();

    private _idToken: string | undefined = undefined;
    private logoutInProgress = false;

    constructor(
        private readonly _authApiService: AuthenticationApiService,
        private readonly _router: Router,
    ) {
        this._router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
            this._checkQueryOnce();
        });
    }

    set headers(value: HeaderDto[]) {
        this._idToken = value.find((h) => h.key === 'x-id-token')?.value;
        this._headerSource.next(value.filter((h) => h.key !== 'x-id-token'));
    }

    set authentication(value: AuthenticationDto) {
        this._authSource.next(value);
        this._adminSource.next(this.isAdmin);
    }

    private _checkQueryOnce(): void {
        if (this._adminSource.value) return; // schon gesetzt
        const stored = sessionStorage.getItem('isAdmin');
        if (stored === 'true') {
            this._adminSource.next(true);
            return;
        }

        const tree = this._router.parseUrl(this._router.url);
        const qp = tree.queryParams ?? {};
        if (qp['isAdmin'] === 'true') {
            this._adminSource.next(true);
            sessionStorage.setItem('isAdmin', 'true');
            this._router.navigate([], {
                queryParams: { isAdmin: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }
    }

    get isAdmin(): boolean {
        const permissions = this._authSource.value?.permissions;
        return (
            Array.isArray(permissions) &&
            permissions.some((permission) => permission.key?.trim() === 'isAdmin' && permission.value === true)
        );
    }

    resetHeaders() {
        this._idToken = undefined;
        this._headerSource.next(undefined);
    }

    goToSsoLoginPage(redirectUri: string) {
        const redirectUriWithoutCode = new URL(redirectUri);
        redirectUriWithoutCode.searchParams.delete('code');

        const basePath = AuthenticationApiService.AuthRedirectSsoLoginPath;
        const uriEncodedRedirectUri = encodeURIComponent(redirectUriWithoutCode.toString());

        window.location.href = `${basePath}?redirect_uri=${uriEncodedRedirectUri}`;
    }

    logoutSso(redirectPath: string = '/') {
        if (this.logoutInProgress) return;

        const idToken = this._idToken;

        this.logoutInProgress = true;
        this.resetHeaders();

        const basePath = AuthenticationApiService.AuthRedirectSsoLogoutPath;
        const redirectUri = encodeURIComponent(`${window.location.origin}${redirectPath}`);
        const traceId = uuidv4();

        let logoutUrl = `${basePath}?post_logout_redirect_uri=${redirectUri}&x-trace-id=${traceId}`;

        if (idToken) {
            logoutUrl += `&id_token_hint=${idToken}`;
        }

        window.location.href = logoutUrl;
    }

    getSsoInit(code: string) {
        return this._authApiService.authSsoAndAuthorizationInit(
            { 'x-trace-id': uuidv4(), code },
            new HttpContext().set(SKIP_HEADER_INTERCEPTOR, true),
        );
    }
}
